import React, { useState, useEffect } from 'react'
import { useAuth } from './Auth/AuthProvider'
import * as FiIcons from 'react-icons/fi'
import SafeIcon from '../common/SafeIcon'
import { motion } from 'framer-motion'
import supabase from '../supabase/supabase'
import * as authService from '../services/authService'
import PageHeader from './Navigation/PageHeader'

const { FiUser, FiMail, FiSave, FiX, FiCamera, FiCheck, FiAlertTriangle, FiLock, FiEye, FiEyeOff } = FiIcons

const UserProfile = () => {
  const { user } = useAuth()
  const [profile, setProfile] = useState(null)
  const [formData, setFormData] = useState({
    full_name: '',
    avatar_url: ''
  })
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(null)
  const [hasChanges, setHasChanges] = useState(false)
  
  // Password change state
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  })
  const [showCurrentPassword, setShowCurrentPassword] = useState(false)
  const [showNewPassword, setShowNewPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [changingPassword, setChangingPassword] = useState(false)
  const [passwordError, setPasswordError] = useState(null)
  const [passwordSuccess, setPasswordSuccess] = useState(null)

  useEffect(() => {
    loadProfile()
  }, [user])

  const loadProfile = async () => {
    try {
      setLoading(true)
      setError(null)

      // Get user's profile from auth_users table
      const { data, error: profileError } = await supabase
        .from('auth_users')
        .select('*')
        .eq('id', user.id)
        .single()

      if (profileError) {
        // If profile doesn't exist, create it
        if (profileError.code === 'PGRST116') {
          const { data: newProfile, error: insertError } = await supabase
            .from('auth_users')
            .insert({
              id: user.id,
              full_name: user.user_metadata?.full_name || '',
              avatar_url: user.user_metadata?.avatar_url || ''
            })
            .select()
            .single()

          if (insertError) throw insertError
          
          setProfile(newProfile)
          setFormData({
            full_name: newProfile.full_name || '',
            avatar_url: newProfile.avatar_url || ''
          })
        } else {
          throw profileError
        }
      } else {
        setProfile(data)
        setFormData({
          full_name: data.full_name || '',
          avatar_url: data.avatar_url || ''
        })
      }
    } catch (err) {
      console.error('Error loading profile:', err)
      setError('Failed to load profile. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }))
    setHasChanges(true)
    setSuccess(null)
    setError(null)
  }

  const handleSave = async () => {
    try {
      setSaving(true)
      setError(null)
      setSuccess(null)

      // Validate
      if (!formData.full_name.trim()) {
        setError('Full name is required')
        return
      }

      // Update profile in auth_users table
      // RLS policy allows users to update their own profile
      const { data, error: updateError } = await supabase
        .from('auth_users')
        .update({
          full_name: formData.full_name.trim(),
          avatar_url: formData.avatar_url.trim() || null,
          updated_at: new Date().toISOString()
        })
        .eq('id', user.id)
        .select()
        .single()

      if (updateError) throw updateError

      setProfile(data)
      setHasChanges(false)
      setSuccess('Profile updated successfully!')

      // Auto-hide success message after 3 seconds
      setTimeout(() => setSuccess(null), 3000)
    } catch (err) {
      console.error('Error updating profile:', err)
      setError(err.message || 'Failed to update profile. Please try again.')
    } finally {
      setSaving(false)
    }
  }

  const handleCancel = () => {
    setFormData({
      full_name: profile?.full_name || '',
      avatar_url: profile?.avatar_url || ''
    })
    setHasChanges(false)
    setError(null)
    setSuccess(null)
  }

  const getInitials = (name) => {
    if (!name) return '?'
    const parts = name.trim().split(' ')
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
    }
    return name.substring(0, 2).toUpperCase()
  }

  const validatePassword = (password) => {
    const errors = []
    if (password.length < 8) errors.push('At least 8 characters')
    if (!/[A-Z]/.test(password)) errors.push('One uppercase letter')
    if (!/[a-z]/.test(password)) errors.push('One lowercase letter')
    if (!/[0-9]/.test(password)) errors.push('One number')
    return errors
  }

  const handlePasswordChange = async () => {
    try {
      setChangingPassword(true)
      setPasswordError(null)
      setPasswordSuccess(null)

      // Validate current password
      if (!passwordData.currentPassword) {
        setPasswordError('Current password is required')
        return
      }

      // Validate new password
      const passwordErrors = validatePassword(passwordData.newPassword)
      if (passwordErrors.length > 0) {
        setPasswordError(`Password must contain: ${passwordErrors.join(', ')}`)
        return
      }

      // Validate passwords match
      if (passwordData.newPassword !== passwordData.confirmPassword) {
        setPasswordError('New passwords do not match')
        return
      }

      // Call the service
      await authService.changePassword(passwordData.currentPassword, passwordData.newPassword)

      // Success
      setPasswordSuccess('Password changed successfully!')
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' })

      // Auto-hide success message after 3 seconds
      setTimeout(() => setPasswordSuccess(null), 3000)
    } catch (err) {
      console.error('Error changing password:', err)
      setPasswordError(err.message || 'Failed to change password. Please try again.')
    } finally {
      setChangingPassword(false)
    }
  }

  const passwordErrors = validatePassword(passwordData.newPassword)
  const passwordStrength = 4 - passwordErrors.length

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="text-center py-12">
          <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-600 font-medium">Loading profile...</p>
        </div>
      </div>
    )
  }

  return (
    <>
      <PageHeader
        title="My Profile"
        description="Manage your personal information and settings"
        icon={FiUser}
      />

      <div className="max-w-4xl mx-auto p-6">
        {/* Alerts */}
        {error && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start space-x-3"
          >
            <SafeIcon icon={FiAlertTriangle} className="text-red-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-red-800 flex-1">{error}</p>
            <button onClick={() => setError(null)} className="text-red-600 hover:text-red-800">
              <SafeIcon icon={FiX} />
            </button>
          </motion.div>
        )}

        {success && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg flex items-start space-x-3"
          >
            <SafeIcon icon={FiCheck} className="text-green-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-green-800 flex-1">{success}</p>
            <button onClick={() => setSuccess(null)} className="text-green-600 hover:text-green-800">
              <SafeIcon icon={FiX} />
            </button>
          </motion.div>
        )}

        {/* Profile Card */}
        <div className="bg-white rounded-lg shadow-lg overflow-hidden">
          {/* Header with Avatar */}
          <div className="bg-gradient-to-r from-blue-600 to-purple-600 px-8 py-12">
            <div className="flex items-center space-x-6">
              {/* Avatar */}
              <div className="relative">
                {formData.avatar_url ? (
                  <img
                    src={formData.avatar_url}
                    alt={formData.full_name}
                    className="w-24 h-24 rounded-full border-4 border-white shadow-lg object-cover"
                    onError={(e) => {
                      e.target.style.display = 'none'
                      e.target.nextSibling.style.display = 'flex'
                    }}
                  />
                ) : null}
                <div
                  className="w-24 h-24 rounded-full border-4 border-white shadow-lg bg-white flex items-center justify-center text-3xl font-bold text-blue-600"
                  style={{ display: formData.avatar_url ? 'none' : 'flex' }}
                >
                  {getInitials(formData.full_name)}
                </div>
              </div>

              {/* User Info */}
              <div className="flex-1">
                <h2 className="text-3xl font-bold text-white mb-2">
                  {formData.full_name || 'No Name Set'}
                </h2>
                <div className="flex items-center space-x-2 text-blue-100">
                  <SafeIcon icon={FiMail} />
                  <span>{user.email}</span>
                </div>
                <p className="text-sm text-blue-200 mt-2">
                  Member since {new Date(profile?.created_at || user.created_at).toLocaleDateString()}
                </p>
              </div>
            </div>
          </div>

          {/* Form */}
          <div className="p-8">
            <h3 className="text-xl font-semibold text-gray-900 mb-6">Profile Information</h3>
            
            <div className="space-y-6">
              {/* Full Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Full Name *
                </label>
                <input
                  type="text"
                  value={formData.full_name}
                  onChange={(e) => handleInputChange('full_name', e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Enter your full name"
                  disabled={saving}
                />
                <p className="text-xs text-gray-500 mt-1">
                  This name will be visible to other team members
                </p>
              </div>

              {/* Email (Read-only) */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email Address
                </label>
                <input
                  type="email"
                  value={user.email}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-gray-50 text-gray-500 cursor-not-allowed"
                  disabled
                />
                <p className="text-xs text-gray-500 mt-1">
                  Email cannot be changed here. Contact support to update your email.
                </p>
              </div>

              {/* Avatar URL */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Avatar URL
                </label>
                <div className="flex space-x-2">
                  <div className="flex-1">
                    <input
                      type="url"
                      value={formData.avatar_url}
                      onChange={(e) => handleInputChange('avatar_url', e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="https://example.com/avatar.jpg"
                      disabled={saving}
                    />
                  </div>
                  <button
                    type="button"
                    className="px-4 py-3 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                    title="Upload avatar"
                    disabled={saving}
                  >
                    <SafeIcon icon={FiCamera} className="text-gray-600" />
                  </button>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Provide a URL to your profile picture or upload one
                </p>
              </div>

              {/* Account Info (Read-only) */}
              <div className="pt-6 border-t border-gray-200">
                <h4 className="text-sm font-medium text-gray-700 mb-4">Account Information</h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-gray-500">User ID</p>
                    <p className="font-mono text-xs text-gray-900 mt-1">{user.id}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Last Updated</p>
                    <p className="text-gray-900 mt-1">
                      {profile?.updated_at 
                        ? new Date(profile.updated_at).toLocaleString()
                        : 'Never'}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            {hasChanges && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="mt-8 flex items-center justify-between p-4 bg-blue-50 border border-blue-200 rounded-lg"
              >
                <p className="text-sm text-blue-800 font-medium">
                  You have unsaved changes
                </p>
                <div className="flex space-x-3">
                  <button
                    onClick={handleCancel}
                    className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-white transition-colors"
                    disabled={saving}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSave}
                    className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                    disabled={saving}
                  >
                    <SafeIcon icon={FiSave} />
                    <span>{saving ? 'Saving...' : 'Save Changes'}</span>
                  </button>
                </div>
              </motion.div>
            )}
          </div>
        </div>

        {/* Change Password Card */}
        <div className="bg-white rounded-lg shadow-lg overflow-hidden mt-6">
          <div className="p-8">
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <SafeIcon icon={FiLock} className="text-blue-600" />
              </div>
              <div>
                <h3 className="text-xl font-semibold text-gray-900">Change Password</h3>
                <p className="text-sm text-gray-500">Update your account password</p>
              </div>
            </div>

            {/* Password Alerts */}
            {passwordError && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start space-x-3"
              >
                <SafeIcon icon={FiAlertTriangle} className="text-red-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-red-800 flex-1">{passwordError}</p>
                <button onClick={() => setPasswordError(null)} className="text-red-600 hover:text-red-800">
                  <SafeIcon icon={FiX} />
                </button>
              </motion.div>
            )}

            {passwordSuccess && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg flex items-start space-x-3"
              >
                <SafeIcon icon={FiCheck} className="text-green-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-green-800 flex-1">{passwordSuccess}</p>
                <button onClick={() => setPasswordSuccess(null)} className="text-green-600 hover:text-green-800">
                  <SafeIcon icon={FiX} />
                </button>
              </motion.div>
            )}

            <div className="space-y-6">
              {/* Current Password */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Current Password *
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <SafeIcon icon={FiLock} className="text-gray-400" />
                  </div>
                  <input
                    type={showCurrentPassword ? 'text' : 'password'}
                    value={passwordData.currentPassword}
                    onChange={(e) => setPasswordData(prev => ({ ...prev, currentPassword: e.target.value }))}
                    className="w-full pl-10 pr-12 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Enter current password"
                    disabled={changingPassword}
                  />
                  <button
                    type="button"
                    onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                    tabIndex={-1}
                  >
                    <SafeIcon icon={showCurrentPassword ? FiEyeOff : FiEye} />
                  </button>
                </div>
              </div>

              {/* New Password */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  New Password *
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <SafeIcon icon={FiLock} className="text-gray-400" />
                  </div>
                  <input
                    type={showNewPassword ? 'text' : 'password'}
                    value={passwordData.newPassword}
                    onChange={(e) => setPasswordData(prev => ({ ...prev, newPassword: e.target.value }))}
                    className="w-full pl-10 pr-12 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Enter new password"
                    disabled={changingPassword}
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                    tabIndex={-1}
                  >
                    <SafeIcon icon={showNewPassword ? FiEyeOff : FiEye} />
                  </button>
                </div>

                {/* Password Strength Indicator */}
                {passwordData.newPassword && (
                  <div className="mt-2">
                    <div className="flex space-x-1 mb-1">
                      {[1, 2, 3, 4].map((level) => (
                        <div
                          key={level}
                          className={`h-1 flex-1 rounded ${
                            passwordStrength >= level
                              ? passwordStrength <= 1
                                ? 'bg-red-500'
                                : passwordStrength <= 2
                                ? 'bg-orange-500'
                                : passwordStrength <= 3
                                ? 'bg-yellow-500'
                                : 'bg-green-500'
                              : 'bg-gray-200'
                          }`}
                        />
                      ))}
                    </div>
                    <p className="text-xs text-gray-500">
                      {passwordStrength <= 1 && 'Weak'}
                      {passwordStrength === 2 && 'Fair'}
                      {passwordStrength === 3 && 'Good'}
                      {passwordStrength === 4 && 'Strong'}
                    </p>
                  </div>
                )}
              </div>

              {/* Confirm New Password */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Confirm New Password *
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <SafeIcon icon={FiLock} className="text-gray-400" />
                  </div>
                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    value={passwordData.confirmPassword}
                    onChange={(e) => setPasswordData(prev => ({ ...prev, confirmPassword: e.target.value }))}
                    className="w-full pl-10 pr-12 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Confirm new password"
                    disabled={changingPassword}
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                    tabIndex={-1}
                  >
                    <SafeIcon icon={showConfirmPassword ? FiEyeOff : FiEye} />
                  </button>
                </div>
                {passwordData.confirmPassword && passwordData.newPassword !== passwordData.confirmPassword && (
                  <p className="text-xs text-red-500 mt-1">Passwords do not match</p>
                )}
                {passwordData.confirmPassword && passwordData.newPassword === passwordData.confirmPassword && (
                  <p className="text-xs text-green-500 mt-1 flex items-center space-x-1">
                    <SafeIcon icon={FiCheck} />
                    <span>Passwords match</span>
                  </p>
                )}
              </div>

              {/* Password Requirements */}
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-sm font-medium text-gray-700 mb-2">Password Requirements:</p>
                <ul className="text-xs text-gray-600 space-y-1">
                  <li className={`flex items-center space-x-2 ${passwordData.newPassword.length >= 8 ? 'text-green-600' : ''}`}>
                    <SafeIcon icon={passwordData.newPassword.length >= 8 ? FiCheck : FiAlertTriangle} className="flex-shrink-0" />
                    <span>At least 8 characters</span>
                  </li>
                  <li className={`flex items-center space-x-2 ${/[A-Z]/.test(passwordData.newPassword) ? 'text-green-600' : ''}`}>
                    <SafeIcon icon={/[A-Z]/.test(passwordData.newPassword) ? FiCheck : FiAlertTriangle} className="flex-shrink-0" />
                    <span>One uppercase letter</span>
                  </li>
                  <li className={`flex items-center space-x-2 ${/[a-z]/.test(passwordData.newPassword) ? 'text-green-600' : ''}`}>
                    <SafeIcon icon={/[a-z]/.test(passwordData.newPassword) ? FiCheck : FiAlertTriangle} className="flex-shrink-0" />
                    <span>One lowercase letter</span>
                  </li>
                  <li className={`flex items-center space-x-2 ${/[0-9]/.test(passwordData.newPassword) ? 'text-green-600' : ''}`}>
                    <SafeIcon icon={/[0-9]/.test(passwordData.newPassword) ? FiCheck : FiAlertTriangle} className="flex-shrink-0" />
                    <span>One number</span>
                  </li>
                </ul>
              </div>

              {/* Change Password Button */}
              <button
                onClick={handlePasswordChange}
                disabled={changingPassword || passwordStrength < 4 || passwordData.newPassword !== passwordData.confirmPassword || !passwordData.currentPassword}
                className="w-full bg-gradient-to-r from-blue-600 to-purple-600 text-white py-3 rounded-lg font-semibold hover:from-blue-700 hover:to-purple-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
              >
                {changingPassword ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    <span>Changing Password...</span>
                  </>
                ) : (
                  <>
                    <SafeIcon icon={FiLock} />
                    <span>Change Password</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

export default UserProfile
